import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SopResponseDto } from '@qc/shared';

import { SopDialogComponent } from '../dialogs/sop-dialog.component';
import { SopFormData } from '../sop-form/sop-form.component';
import { SopService } from '@/app/core/services/sop.service';

export interface SopWithRelationship {
    sop: SopResponseDto;
    relationshipId?: string;
}

@Component({
    selector: 'qc-sop-manager',
    standalone: true,
    imports: [CommonModule, ButtonModule, ConfirmDialogModule, ToastModule],
    providers: [ConfirmationService, MessageService],
    template: `
        <div class="space-y-4">
            <div class="flex justify-between items-center">
                <label class="block text-sm font-medium">
                    {{ label() }}
                    <span *ngIf="required()" class="text-red-500 ml-1">*</span>
                </label>
                <p-button
                    severity="secondary"
                    variant="outlined"
                    size="small"
                    icon="pi pi-plus"
                    label="Add SOP"
                    (click)="showAddSopDialog()"
                    [disabled]="disabled()"
                />
            </div>

            <div
                *ngIf="sops().length === 0"
                class="text-sm text-gray-500 italic border-2 border-dashed border-gray-200 rounded-lg p-4 text-center"
            >
                No SOPs assigned yet. Click "Add SOP" to get started.
            </div>

            <div
                *ngFor="let sopWithRel of sops(); let i = index"
                class="border border-gray-200 rounded-lg p-4 bg-white"
            >
                <div *ngIf="sopWithRel.sop" class="flex items-start justify-between">
                    <div class="flex-1">
                        <h4 class="font-medium text-gray-900">{{ sopWithRel.sop.sopNumber }}</h4>
                        <p *ngIf="sopWithRel.sop.sopTitle" class="text-sm text-gray-600">
                            {{ sopWithRel.sop.sopTitle }}
                        </p>
                        <p *ngIf="sopWithRel.sop.managedBy" class="text-sm text-gray-600">
                            Managed by: {{ sopWithRel.sop.managedBy }}
                        </p>
                        <p *ngIf="sopWithRel.sop.context" class="text-sm text-blue-600">
                            Context: {{ sopWithRel.sop.context }}
                        </p>
                    </div>
                    <div class="flex items-center space-x-2">
                        <p-button
                            severity="secondary"
                            variant="text"
                            size="small"
                            icon="pi pi-pencil"
                            (click)="editSop(sopWithRel.sop)"
                            title="Edit SOP"
                            [disabled]="disabled()"
                        />
                        <p-button
                            severity="danger"
                            variant="text"
                            size="small"
                            icon="pi pi-trash"
                            (click)="removeSop(sopWithRel.sop.id)"
                            title="Remove SOP"
                            [disabled]="disabled()"
                        />
                    </div>
                </div>
                <div *ngIf="!sopWithRel.sop" class="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    Invalid SOP data - unable to display
                </div>
            </div>
        </div>

        <p-confirmDialog header="Confirm Delete" icon="pi pi-exclamation-triangle"></p-confirmDialog>
        <p-toast position="bottom-right"></p-toast>
    `,
})
export class SopManagerComponent implements OnInit, OnDestroy {
    private dialog = inject(Dialog);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);
    private sopService = inject(SopService);
    private destroy$ = new Subject<void>();

    label = input<string>('SOPs');
    required = input<boolean>(false);
    disabled = input<boolean>(false);
    deferredSave = input<boolean>(false);
    initialSops = input<SopWithRelationship[]>([]);

    sops = signal<SopWithRelationship[]>([]);
    sopsUpdated = output<SopWithRelationship[]>();
    sopAdded = output<SopWithRelationship>();
    sopRemoved = output<string>();

    ngOnInit() {
        this.syncFromInitialSops();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['initialSops']) {
            this.syncFromInitialSops();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private syncFromInitialSops(): void {
        const initial = this.initialSops();
        if (initial && initial.length > 0) {
            const validSops = initial.filter(
                (sopWithRel) => sopWithRel && sopWithRel.sop && typeof sopWithRel.sop === 'object',
            );
            this.sops.set([...validSops]);
            return;
        }

       
        if (this.sops().length > 0) {
            return;
        }

        this.sops.set([]);
    }

    showAddSopDialog() {
        this.openSopDialog();
    }

    editSop(sop: SopResponseDto) {
        this.openSopDialog(sop);
    }

    private openSopDialog(sopToEdit?: SopResponseDto) {
        const dialogRef = this.dialog.open(SopDialogComponent, {
            hasBackdrop: true,
            disableClose: false,
            autoFocus: false,
        });
        const componentInstance = dialogRef.componentInstance! as SopDialogComponent;

        if (sopToEdit) {
            componentInstance.initialData = sopToEdit;
        }

        componentInstance.deferredSave = this.deferredSave();

        componentInstance.sopSaved.pipe(takeUntil(this.destroy$)).subscribe((savedSop: SopResponseDto) => {
            const sopWithRel: SopWithRelationship = {
                sop: savedSop,
            };

            if (sopToEdit) {
                this.updateSopInList(sopToEdit.id, savedSop);
            } else {
                this.addSopToList(sopWithRel);
            }
        });
    }

    private addSopToList(sopWithRel: SopWithRelationship) {
        if (!sopWithRel || !sopWithRel.sop) {
            console.warn('Attempted to add invalid SOP data');
            return;
        }

        const currentSops = this.sops();
        const updatedSops = [...currentSops, sopWithRel];
        this.sops.set(updatedSops);
        this.sopAdded.emit(sopWithRel);
        this.sopsUpdated.emit(updatedSops);
    }

    private updateSopInList(oldSopId: string, newSop: SopResponseDto) {
        const currentSops = this.sops();
        const updatedSops = currentSops.map((sopWithRel) =>
            sopWithRel.sop && sopWithRel.sop.id === oldSopId ? { ...sopWithRel, sop: newSop } : sopWithRel,
        );
        this.sops.set(updatedSops);
        this.sopsUpdated.emit(updatedSops);
    }

    removeSop(sopId: string) {
        const currentSops = this.sops();
        const sopToRemove = currentSops.find((sopWithRel) => sopWithRel.sop && sopWithRel.sop.id === sopId);

        if (!sopToRemove || !sopToRemove.sop) return;

        if (this.deferredSave()) {
            const updatedSops = currentSops.filter((sopWithRel) => sopWithRel.sop && sopWithRel.sop.id !== sopId);
            this.sops.set(updatedSops);
            this.sopRemoved.emit(sopId);
            this.sopsUpdated.emit(updatedSops);
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete SOP "${sopToRemove.sop.sopNumber}"? This action cannot be undone.`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.sopService
                    .deleteSop(sopId)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            const updatedSops = currentSops.filter((sopWithRel) => sopWithRel.sop && sopWithRel.sop.id !== sopId);
                            this.sops.set(updatedSops);
                            this.sopRemoved.emit(sopId);
                            this.sopsUpdated.emit(updatedSops);

                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'SOP deleted successfully',
                            });
                        },
                        error: (error) => {
                            console.error('Error deleting SOP:', error);
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Failed to delete SOP',
                            });
                        },
                    });
            },
        });
    }
}

